import React, { forwardRef, Ref, useImperativeHandle, useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import Spacer from '@fenderdigital/ui/Spacer';
import BodyText from '@fenderdigital/ui/BodyText';
import Spinner from '@fenderdigital/ui/Spinner';
import { ButtonBase as IconButton } from '@fenderdigital/ui/Button';
import { LightningBolt } from '@fenderdigital/ui/Icons';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { EventNames, trackEvent } from '../../lib/analytics';
import ToolTipModal from '../ToolTipModal';
import { fetchStreaks } from '../../lib/streaks';
import * as S from './StreaksButton-styled';

export type StreaksButtonTooltipHandle = {
  showTooltip: () => void;
  hideTooltip: () => void;
};

function getButtonAriaLabel(streakCount: number | null): string {
  if (streakCount === null) {
    return 'Loading';
  }
  return `Open Streaks. Current streak: ${streakCount} ${streakCount > 1 ? 'days' : 'day'}`;
}

interface StreaksButtonProps {
  isBackgroundTransparent?: boolean;
  ref?: Ref<StreaksButtonTooltipHandle>;
}

const StreaksButton: React.FC<StreaksButtonProps> = forwardRef(
  ({ isBackgroundTransparent }, ref) => {
    const { fontSizes } = useTheme();
    const { user } = usePlayUser();
    const [isTooltipModalVisible, setIsTooltipModalVisible] = useState(false);
    const [streakCount, setStreakCount] = useState<number | null>(null);
    const [shouldFetch, setShouldFetch] = useState(true);

    useImperativeHandle(ref, () => ({
      showTooltip(): void {
        setIsTooltipModalVisible(true);
      },
      hideTooltip(): void {
        setIsTooltipModalVisible(false);
      },
    }));

    useEffect(() => {
      const timeoutIds: number[] = [];

      // If streak data is stale, predict streak and retry the API 3 times
      const getStreak = async (retriesLeft = 2): Promise<void> => {
        const streakObj = await fetchStreaks();

        if (streakObj) {
          setStreakCount(streakObj.currentStreak);
          if (streakObj.retry && retriesLeft > 0) {
            const timeoutId = setTimeout(getStreak(--retriesLeft), 2000);
            timeoutIds.push(timeoutId);
          } else {
            setShouldFetch(false);
          }
        }
      };

      // We only need to fetch once a session
      if (shouldFetch) {
        getStreak();
      }
      return (): void => {
        timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
      };
    }, [shouldFetch]);

    const handleClick = (): void => {
      showTooltipModal();

      const trackingProperties = {
        // eslint-disable-next-line @typescript-eslint/camelcase
        fc_id: user?.userID,
      };

      trackEvent(EventNames.STREAK_ICON_CLICKED, trackingProperties);
    };

    const showTooltipModal = (): void => {
      setIsTooltipModalVisible(true);
    };

    const hideTooltipModal = (): void => {
      setIsTooltipModalVisible(false);
    };

    return (
      <S.StreaksButtonContainer isBgTransparent={isBackgroundTransparent}>
        <IconButton
          data-id="streaks-button"
          size="small"
          variant="ghost"
          hoverColor="transparent"
          aria-label={getButtonAriaLabel(streakCount)}
          onClick={handleClick}
        >
          <LightningBolt data-id="streaks-icon" aria-hidden="true" fontSize={fontSizes.f8} />
          <Spacer direction="vertical" spacing={['sm2', null, 'sm3']} />
          <S.CounterContainer>
            {streakCount !== null ? (
              <BodyText
                dataId="streak-count"
                fontFamily="futuraBook"
                textColor={isBackgroundTransparent ? 'white' : 'black'}
                textSize={['f1', null, 'f2']}
                css={`
                  letter-spacing: 0.02em;
                  line-height: 1.34;
                  font-weight: 600;
                `}
              >
                {streakCount}
              </BodyText>
            ) : (
              <Spinner
                width="1rem"
                strokeBaseColor={isBackgroundTransparent ? 'white' : 'black'}
                strokeColor="transparent"
                strokeSize="1px"
              />
            )}
          </S.CounterContainer>
        </IconButton>
        {isTooltipModalVisible && (
          <ToolTipModal
            headline="Your streak count"
            bodyText="Check in with Fender Play every day to make progress and maintain your streak."
            ctaButtonTitle="Learn more"
            ctaAction={hideTooltipModal}
            ctaActionLink={`${process.env.REACT_APP_CLIENT_FENDER_COM}/whats-new/streaks-updates`}
            target="_blank"
            css={S.ToolTipCSS}
          />
        )}
      </S.StreaksButtonContainer>
    );
  },
);

export default StreaksButton;
